<template>
  <div
    v-if="isAuthenticated"
    class="dast-promo-block"
    :class="{ 'dast-promo-block--collapsed': isCollapsed }"
    @click="isCollapsed && toggleCollapse()"
  >
    <div class="dast-promo-toggle" @click.stop="toggleCollapse" aria-label="Toggle sidebar">
      {{ isCollapsed ? '↑' : '↓' }}
    </div>
    <h2 class="dast-promo-title">DAST is here!</h2>
    <p class="dast-promo-text">
      <span class="dast-promo-highlight-line"> Start a new scan to uncover </span>
      <br />
      <span class="dast-promo-highlight-line"> runtime vulnerabilities </span>
    </p>
    <a :href="dastScanRoute" class="dast-promo-button" target="_blank" rel="noopener noreferrer">
      Learn how it works
      <div class="dast-promo-button-corner dast-promo-button-corner--top-left"></div>
      <div class="dast-promo-button-corner dast-promo-button-corner--bottom-left"></div>
      <div class="dast-promo-button-corner dast-promo-button-corner--top-right"></div>
      <div class="dast-promo-button-corner dast-promo-button-corner--bottom-right"></div>
    </a>
    <div class="dast-promo-features">
      <DastPromoFeature
        class="dast-promo-feature--poc"
        :icon="LightBulbIcon"
        text="Proof of Concept"
      />
      <DastPromoFeature
        class="dast-promo-feature--screencasts"
        :icon="VideoFileIcon"
        text="Screencasts of Devices"
      />
      <DastPromoFeature
        class="dast-promo-feature--stack-traces"
        :icon="PhoneCodeIcon"
        text="Stack Traces"
      />
      <PhoneScreenSolidMock
        :fill="'rgba(255, 255, 255, 0.2)'"
        class="dast-promo-feature--phone-screen-mock"
      />
    </div>
  </div>
</template>

<script>
import LightBulbIcon from '@/assets/iconTemplates/LightBulbIcon.vue';
import VideoFileIcon from '@/assets/iconTemplates/VideoFileIcon.vue';
import PhoneCodeIcon from '@/assets/iconTemplates/PhoneCodeIcon.vue';
import PhoneScreenSolidMock from '@/assets/iconTemplates/PhoneScreenSolidMock.vue';
import DastPromoFeature from './DastPromoFeature.vue';

const STORAGE_KEY = 'dastPromoBlockCollapsed';

export default {
  name: 'DastPromoBlock',
  components: {
    LightBulbIcon,
    VideoFileIcon,
    PhoneCodeIcon,
    PhoneScreenSolidMock,
    DastPromoFeature,
  },
  data() {
    return {
      isCollapsed: false,
      LightBulbIcon,
      VideoFileIcon,
      PhoneCodeIcon,
    };
  },
  created() {
    const savedState = localStorage.getItem(STORAGE_KEY);
    if (savedState !== null) {
      this.isCollapsed = savedState === 'true';
    }
  },
  computed: {
    isAuthenticated() {
      return this.$auth.isAuthenticated;
    },
    dastScanRoute() {
      const DAST_LANDING_URL =
        'https://dast.oversecured.com/?utm_source=website&utm_medium=banner&utm_campaign=dast&utm_content=sia';

      return DAST_LANDING_URL;
    },
  },
  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      localStorage.setItem(STORAGE_KEY, String(this.isCollapsed));
    },
  },
};
</script>

<style lang="scss" scoped>
.dast-promo-features {
  margin-top: 50px;
  display: grid;
  gap: 3rem;
  padding-top: 75px;

  .dast-promo-feature {
    z-index: 10;
  }

  .dast-promo-feature--phone-screen-mock {
    position: absolute;
    z-index: 0;
    left: 0;
    bottom: 0;
    width: 100%;
  }

  .dast-promo-feature--poc {
    justify-self: end;
    margin-right: -12px;
  }
  .dast-promo-feature--screencasts {
    justify-self: start;
    margin-left: -10px;
  }
  .dast-promo-feature--stack-traces {
    justify-self: end;
    margin-right: -2px;
  }
}
.dast-promo-block {
  position: fixed;
  bottom: 0;
  right: 16px;
  width: 300px;
  height: 630px;
  background-color: #1a59f6;
  padding: 1.5rem 1.5rem 0 1.5rem;
  display: flex;
  flex-direction: column;
  z-index: 10;
  overflow: visible;
  transition: all 0.2s ease-in-out;
  transform: translateY(0);
  box-shadow: -4px 4px 16px 0px rgba(0, 0, 0, 0.2);

  $toggle-size: 32px;
  $toggle-font-size: 16px;

  .dast-promo-toggle {
    position: absolute;
    top: $toggle-size / 2;
    left: calc(100% - $toggle-size / 2);
    transform: translateX(-100%);
    width: $toggle-size;
    height: $toggle-size;
    box-shadow: -2px 2px 4px 0px rgba(0, 0, 0, 0.2);
    background-color: #1a59f6;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: $toggle-font-size;
    font-family: 'Aspekta', sans-serif;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    z-index: 1;
  }

  .dast-promo-image {
    min-width: calc(100% + 4rem);
    height: auto;
    margin-top: auto;
    margin-left: -2rem;
    margin-right: -2rem;
    margin-bottom: -2rem;
    object-fit: contain;
  }

  .dast-promo-title {
    font-family: 'Aspekta', sans-serif;
    font-weight: 400;
    font-size: 32px;
    line-height: 1.2;
    color: white;
    margin: 0 0 12px 0;
    transition: all 0.2s ease-in-out;
  }

  .dast-promo-text {
    font-family: 'Aspekta', sans-serif;
    font-weight: 400;
    font-size: 18px;
    line-height: 1.4;
    color: white;
    margin: 0 0 2rem 0;

    .dast-promo-highlight-line {
      background-color: #5c8bfe;
      display: inline-block;
      padding: 2px 4px;
    }
  }

  &--collapsed {
    transform: translateY(calc(100% - 48px));
    padding: 1rem 1rem 0 1rem;
    cursor: pointer;

    .dast-promo-toggle {
      box-shadow: none;
      top: $toggle-size / 4 + 2px;
      left: calc(100% - $toggle-size / 4);
    }

    .dast-promo-title {
      font-size: 18px;
      font-weight: 500;
    }
  }
}

$button-corners-gap: 6px;
.dast-promo-button {
  position: relative;
  display: inline-block;
  padding: 9px 16px;
  background-color: white;
  color: #242424;
  text-decoration: none;
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.01rem;
  transition: all 0.2s ease-in-out;
  width: fit-content;
  margin-left: $button-corners-gap;

  &:hover {
    background-color: rgba(255, 255, 255, 0.16);
    color: white;
  }
}

.dast-promo-button-corner {
  position: absolute;
  width: 8px;
  height: 8px;

  &--top-left {
    top: -$button-corners-gap;
    left: -$button-corners-gap;
    border-left: 1px solid white;
    border-top: 1px solid white;
  }

  &--bottom-left {
    bottom: -$button-corners-gap;
    left: -$button-corners-gap;
    border-left: 1px solid white;
    border-bottom: 1px solid white;
  }

  &--top-right {
    top: -$button-corners-gap;
    right: -$button-corners-gap;
    border-right: 1px solid white;
    border-top: 1px solid white;
  }

  &--bottom-right {
    bottom: -$button-corners-gap;
    right: -$button-corners-gap;
    border-right: 1px solid white;
    border-bottom: 1px solid white;
  }
}
</style>
